"use strict";

import axios from "axios";
import Environment from "../helpers/Environment";

export class MapLegendGenerator {
  static TIMEOUTS = {
    API: 10000,
    CACHE: 3600000,
  };

  static ERRORS = {
    INVALID_CONFIG: "Invalid configuration structure",
    MISSING_CONTAINER: "Legend container not found",
    API_FAILED: "Failed to fetch legend config",
    INVALID_COLOR: "Invalid color format",
    SVG_LOAD_FAILED: "Failed to load SVG template",
  };

  static STORAGE_KEYS = {
    CONFIG: "nperf_map_legend_config",
    TIMESTAMP: "nperf_map_legend_config_timestamp",
  };

  constructor() {
    this.memoryCache = null;
    this.memoryCacheTimestamp = null;
    this.svgCache = new Map();

    this.svgTemplates = {
      nosignal: "/website/img/v2/map-legend/icon-no-signal.svg",
      "2G": "/website/img/v2/map-legend/icon-2G.svg",
      "3G": "/website/img/v2/map-legend/icon-3G.svg",
      "4G": "/website/img/v2/map-legend/icon-4G.svg",
      "4G+": "/website/img/v2/map-legend/icon-4G+.svg",
      "5G": "/website/img/v2/map-legend/icon-5G.svg",
    };
  }

  argbToHex(argbColor) {
    if (!argbColor || typeof argbColor !== "string") {
      throw new Error(`${MapLegendGenerator.ERRORS.INVALID_COLOR}: ${argbColor}`);
    }

    const hex = argbColor.replace("#", "").toUpperCase();

    if (!/^[0-9A-F]{8}$/.test(hex)) {
      throw new Error(`${MapLegendGenerator.ERRORS.INVALID_COLOR}: ${argbColor}`);
    }

    return "#" + hex.substring(2) + hex.substring(0, 2);
  }

  safeLocalStorageGet(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  safeLocalStorageSet(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  safeLocalStorageRemove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  validateConfig(config) {
    try {
      if (
        !config?.caption?.signal?.bubbles ||
        !Array.isArray(config.caption.signal.bubbles)
      ) {
        throw new Error("Invalid signal bubbles structure");
      }

      if (
        !config?.caption?.download?.gradient ||
        !Array.isArray(config.caption.download.gradient)
      ) {
        throw new Error("Invalid download gradient structure");
      }

      config.caption.signal.bubbles.forEach((bubble, index) => {
        if (!bubble.type) {
          throw new Error(`Bubble ${index}: missing type`);
        }
        if (!bubble.mainColor) {
          throw new Error(`Bubble ${index}: missing mainColor`);
        }

        try {
          this.argbToHex(bubble.mainColor);
        } catch (e) {
          throw new Error(`Bubble ${index}: invalid mainColor format`);
        }

        if (bubble.type === "cellgen") {
          if (!bubble.label) {
            throw new Error(`Bubble ${index}: cellgen type requires label`);
          }
          if (!bubble.barColors) {
            throw new Error(`Bubble ${index}: cellgen type requires barColors`);
          }

          ["b1", "b2", "b3", "b4"].forEach((bar) => {
            if (!bubble.barColors[bar]) {
              throw new Error(`Bubble ${index}: missing barColor ${bar}`);
            }
            try {
              this.argbToHex(bubble.barColors[bar]);
            } catch (e) {
              throw new Error(
                `Bubble ${index}: invalid barColor ${bar} format`
              );
            }
          });
        }
      });

      config.caption.download.gradient.forEach((item, index) => {
        if (!item.label) {
          throw new Error(`Gradient ${index}: missing label`);
        }
        if (!item.color) {
          throw new Error(`Gradient ${index}: missing color`);
        }
        try {
          this.argbToHex(item.color);
        } catch (e) {
          throw new Error(`Gradient ${index}: invalid color format`);
        }
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  async getConfig() {
    const now = Date.now();

    if (this.memoryCache && this.memoryCacheTimestamp) {
      if (now - this.memoryCacheTimestamp < MapLegendGenerator.TIMEOUTS.CACHE) {
        return this.memoryCache;
      }
    }

    const cachedConfig = this.safeLocalStorageGet(
      MapLegendGenerator.STORAGE_KEYS.CONFIG
    );
    const cachedTimestamp = this.safeLocalStorageGet(
      MapLegendGenerator.STORAGE_KEYS.TIMESTAMP
    );

    if (cachedConfig && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10);

      if (
        !isNaN(timestamp) &&
        now - timestamp < MapLegendGenerator.TIMEOUTS.CACHE
      ) {
        try {
          const config = JSON.parse(cachedConfig);

          if (this.validateConfig(config)) {
            this.memoryCache = config;
            this.memoryCacheTimestamp = timestamp;
            return config;
          }
        } catch (error) {
          this.safeLocalStorageRemove(MapLegendGenerator.STORAGE_KEYS.CONFIG);
        }
      }
    }

    try {
      const response = await axios.get(Environment.getYuffieWs() + "/config", {
        params: {
          appPlatform: "web-html5",
          appVersion: process.env.APP_VERSION,
        },
        timeout: MapLegendGenerator.TIMEOUTS.API,
      });

      const config = response.data;

      if (!this.validateConfig(config)) {
        return this.getDefaultConfig();
      }

      this.memoryCache = config;
      this.memoryCacheTimestamp = now;

      this.safeLocalStorageSet(
        MapLegendGenerator.STORAGE_KEYS.CONFIG,
        JSON.stringify(config)
      );
      this.safeLocalStorageSet(
        MapLegendGenerator.STORAGE_KEYS.TIMESTAMP,
        now.toString()
      );

      return config;
    } catch (error) {
      if (cachedConfig) {
        try {
          const config = JSON.parse(cachedConfig);
          if (this.validateConfig(config)) {
            return config;
          }
        } catch (parseError) {
          // Sentry will automatically capture the error
        }
      }

      return this.getDefaultConfig();
    }
  }

  getDefaultConfig() {
    return {
      zoomLevel: {
        min: 3,
        max: 16,
      },
      countStatus: "OK",
      caption: {
        signal: {
          bubbles: [
            {
              type: "nosignal",
              mainColor: "#FF565555",
              barColors: {
                b1: "#FFFFFFFF",
                b2: "#FFFFFFFF",
                b3: "#FFFFFFFF",
                b4: "#FFFFFFFF",
              },
            },
            {
              type: "cellgen",
              label: "2G",
              mainColor: "#FF0B66AA",
              barColors: {
                b1: "#FF5ABEE5",
                b2: "#FF3399CC",
                b3: "#FF1C7EBA",
                b4: "#FF0B66AA",
              },
            },
            {
              type: "cellgen",
              label: "3G",
              mainColor: "#FF83C400",
              barColors: {
                b1: "#FFEFFC8A",
                b2: "#FFC5E14E",
                b3: "#FFA7D22A",
                b4: "#FF92C11A",
              },
            },
            {
              type: "cellgen",
              label: "4G",
              mainColor: "#FFFF6E0D",
              barColors: {
                b1: "#FFFFBC8F",
                b2: "#FFFE9D5C",
                b3: "#FFFE8331",
                b4: "#FFFF6E0D",
              },
            },
            {
              type: "cellgen",
              label: "4G+",
              mainColor: "#FFCD0013",
              barColors: {
                b1: "#FFECA6AC",
                b2: "#FFE97B7E",
                b3: "#FFD24750",
                b4: "#FFBC1421",
              },
            },
            {
              type: "cellgen",
              label: "5G",
              mainColor: "#FF9004F4",
              barColors: {
                b1: "#FFD4A8FB",
                b2: "#FFBC70F9",
                b3: "#FFA345F4",
                b4: "#FF9415F4",
              },
            },
          ],
        },
        download: {
          gradient: [
            {
              label: "0",
              color: "#FF125996",
            },
            {
              label: "5M",
              color: "#FF3FA43F",
            },
            {
              label: "20M",
              color: "#FFECA42C",
            },
            {
              label: "80M",
              color: "#FFE6491D",
            },
            {
              label: "1G",
              color: "#FFDF0F26",
            },
          ],
        },
      },
    };
  }

  async loadSvgTemplate(label) {
    if (this.svgCache.has(label)) {
      return this.svgCache.get(label);
    }

    const svgPath = this.svgTemplates[label];

    if (!svgPath) {
      return null;
    }

    const response = await fetch(svgPath);
    if (!response.ok) {
      throw new Error(`Failed to load SVG: ${svgPath} (HTTP ${response.status})`);
    }
    const svgText = await response.text();

    this.svgCache.set(label, svgText);

    return svgText;
  }

  async generateSignalBubbleSvg(bubble) {
    const { type, label, mainColor, barColors } = bubble;

    const templateKey = type === "nosignal" ? "nosignal" : label;

    let svgTemplate;
    try {
      svgTemplate = await this.loadSvgTemplate(templateKey);
    } catch (error) {
      return this.generateFallbackSvg();
    }

    if (!svgTemplate) {
      return this.generateFallbackSvg();
    }

    try {
      const bgColor = "#F2F7FE";
      const capColor = this.argbToHex(mainColor);
      const borderColor = capColor;

      let processedSvg = svgTemplate;

      if (type === "nosignal") {
        processedSvg = processedSvg.replace(/var\(--bg-color\)/g, capColor);
      } else {
        const bar1Color = this.argbToHex(barColors.b1);
        const bar2Color = this.argbToHex(barColors.b2);
        const bar3Color = this.argbToHex(barColors.b3);
        const bar4Color = this.argbToHex(barColors.b4);

        processedSvg = processedSvg
          .replace(/var\(--bg-color\)/g, bgColor)
          .replace(/var\(--cap-color\)/g, capColor)
          .replace(/var\(--border-color\)/g, borderColor)
          .replace(/var\(--bar1-color\)/g, bar1Color)
          .replace(/var\(--bar2-color\)/g, bar2Color)
          .replace(/var\(--bar3-color\)/g, bar3Color)
          .replace(/var\(--bar4-color\)/g, bar4Color)
          .replace(/var\(--text-color\)/g, bgColor);
      }

      return processedSvg;
    } catch (error) {
      return this.generateFallbackSvg();
    }
  }

  generateFallbackSvg() {
    return `
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="#cccccc"/>
        <text x="20" y="25" text-anchor="middle" fill="white" font-size="12">?</text>
      </svg>
    `;
  }

  generateDownloadGradient(gradientData) {
    const width = 250;
    const height = 35;
    const steps = gradientData.length;

    try {
      const gradientStops = gradientData
        .map((item, index) => {
          const offset = (index / (steps - 1)) * 100;
          const hexColor = this.argbToHex(item.color);
          return `<stop offset="${offset}%" stop-color="${hexColor}"/>`;
        })
        .join("\n                        ");

      const labels = gradientData
        .map((item, index) => {
          let adjustedX = (index / (steps - 1)) * width;
          let textAnchor = "middle";

          if (index === 0) {
            adjustedX = 0;
            textAnchor = "start";
          } else if (index === steps - 1) {
            adjustedX = width;
            textAnchor = "end";
          }

          return `<text x="${adjustedX}" y="11" text-anchor="${textAnchor}" fill="currentColor" font-size="10">${item.label}</text>`;
        })
        .join("\n                    ");

      return `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="downloadGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          ${gradientStops}
        </linearGradient>
      </defs>
      <rect y="17" width="${width}" height="15" fill="url(#downloadGradient)" rx="2"/>
      <g>
        ${labels}
      </g>
    </svg>
  `;
    } catch (error) {
      return this.generateFallbackGradient();
    }
  }

  generateFallbackGradient() {
    return `
      <svg width="250" height="35" viewBox="0 0 250 35" xmlns="http://www.w3.org/2000/svg">
        <rect y="17" width="250" height="15" fill="#cccccc" rx="2"/>
        <text x="125" y="11" text-anchor="middle" fill="currentColor" font-size="10">Gradient unavailable</text>
      </svg>
    `;
  }

  async generateLegendHtml(mapConnectionType) {
    const config = await this.getConfig();
    const caption = config.caption;

    if (mapConnectionType === "5G") {
      const bubble5G = caption.signal.bubbles.find((b) => b.label === "5G");

      if (!bubble5G) {
        return `<div class="container-legend-img" id="containerLegendDownload">
          <div class="icon-download">${this.generateFallbackSvg()}</div>
        </div>`;
      }

      const svgContent = await this.generateSignalBubbleSvg(bubble5G);

      return `
        <div class="container-legend-img" id="containerLegendDownload">
          <div class="icon-download">
            ${svgContent}
          </div>
        </div>
      `;
    } else {
      const signalBubblesPromises = caption.signal.bubbles.map(
        async (bubble) => {
          const svgContent = await this.generateSignalBubbleSvg(bubble);
          return `
          <div class="icon-signal">
            ${svgContent}
          </div>
        `;
        }
      );

      const signalBubbles = (await Promise.all(signalBubblesPromises)).join("");
      const downloadGradient = this.generateDownloadGradient(
        caption.download.gradient
      );

      return `
        <div class="container-legend-img" id="containerLegendSignal">
          ${signalBubbles}
        </div>
        <div class="container-legend-img" id="containerLegendDownload">
          <div class="icon-download">
            ${downloadGradient}
          </div>
        </div>
      `;
    }
  }

  async renderLegend(containerSelector, mapConnectionType) {
    const container = document.querySelector(containerSelector);

    if (!container) {
      return;
    }

    try {
      const legendHtml = await this.generateLegendHtml(mapConnectionType);

      const template = document.createElement("template");
      template.innerHTML = legendHtml.trim();

      container.replaceChildren(template.content);
    } catch (error) {
      container.innerHTML = `<div class="legend-error">Failed to load legend</div>`;
    }
  }

  clearCache() {
    this.memoryCache = null;
    this.memoryCacheTimestamp = null;
    this.svgCache.clear();

    this.safeLocalStorageRemove(MapLegendGenerator.STORAGE_KEYS.CONFIG);
    this.safeLocalStorageRemove(MapLegendGenerator.STORAGE_KEYS.TIMESTAMP);
  }

  async refreshConfig() {
    this.clearCache();
    return await this.getConfig();
  }
}
